import type { AdminJobTiming } from "@/api/adminJobTiming";
import type { DiagnosticJobStatus } from "@/api/adminDiagnosticJob";
import type { DatasetUploadStatus } from "@/api/datasetUpload";
import type { WorkerStatusTableRow } from "./WorkerStatusTableRow";
import { parseUtcMs } from "@/utils/datetime";

export type TimelinePhase = "waiting" | "execution" | "finished" | "lastReport";
export type TimelineSegment = { phase: TimelinePhase; start: number; end: number };
export type WorkerTimeline = { segments: readonly TimelineSegment[]; note: string | null };
export type TimelineRange = { start: number; end: number };

type JobStatus = DatasetUploadStatus | DiagnosticJobStatus;
const terminalStatuses = new Set<JobStatus>([
  "done",
  "succeeded",
  "partial_failed",
  "failed",
  "cancelled",
  "duplicate_skipped",
  "conflict",
]);

/** 미기록 시각과 종료 상태를 함께 읽어 끝난 작업의 막대가 계속 늘어나는 것을 막는다. */
export function jobTimeline(
  job: AdminJobTiming & { status: JobStatus },
  now: number,
): WorkerTimeline {
  const queued = parseUtcMs(job.queued_at);
  const started = parseUtcMs(job.started_at);
  const finished = parseUtcMs(job.finished_at);
  const terminal = terminalStatuses.has(job.status);
  if (
    (queued !== null && started !== null && started < queued) ||
    (started !== null && finished !== null && finished < started) ||
    (queued !== null && finished !== null && finished < queued)
  )
    return { segments: [], note: "시각 기록 확인 필요" };

  const segments: TimelineSegment[] = [];
  const end = finished ?? (terminal ? null : now);
  if (queued !== null && started !== null) {
    segments.push({ phase: "waiting", start: queued, end: started });
  } else if (
    queued !== null &&
    end !== null &&
    (job.status === "queued" || job.status === "uploaded" || finished !== null)
  ) {
    segments.push({ phase: "waiting", start: queued, end: Math.max(queued, end) });
  }
  if (started !== null) {
    segments.push({ phase: "execution", start: started, end: Math.max(started, end ?? started) });
  }
  if (finished !== null) segments.push({ phase: "finished", start: finished, end: finished });
  const note =
    terminal && finished === null
      ? "종료 시각 미기록"
      : segments.length === 0
        ? job.status === "pending_upload"
          ? "파일 전송 대기"
          : "실행 시각 미기록"
        : null;
  return { segments, note };
}

/** Offline Worker는 마지막 보고까지만 표시한다. 종료 시각으로 해석하지 않는다. */
export function workerTimeline(row: WorkerStatusTableRow, now: number): WorkerTimeline {
  if (row.current_job === null)
    return { segments: [], note: row.online ? "처리 중인 작업 없음" : "마지막 보고에 작업 없음" };
  const start = parseUtcMs(row.current_job.started_at);
  if (start === null) return { segments: [], note: "시작 시각 미기록" };
  const end = row.online ? now : parseUtcMs(row.reported_at);
  if (end === null || end < start) return { segments: [], note: "시각 기록 확인 필요" };
  return {
    segments: [{ phase: row.online ? "execution" : "lastReport", start, end }],
    note: row.online ? null : "마지막 보고까지 표시",
  };
}

/** 같은 페이지의 모든 행에 같은 실제 시간축을 적용한다. */
export function timelineRange(timelines: readonly WorkerTimeline[], now: number): TimelineRange {
  const segments = timelines.flatMap((item) => item.segments);
  if (segments.length === 0) return { start: now - 60_000, end: now };
  const start = Math.min(...segments.map((segment) => segment.start));
  const end = Math.max(now, ...segments.map((segment) => segment.end));
  const padding = Math.max(60_000, (end - start) * 0.05);
  return { start: start - padding, end: Math.max(start + 60_000, end + padding) };
}

/** 읽을 수 있는 간격으로 같은 시각의 세로선을 모든 작업에 적용한다. */
export function timelineTicks(range: TimelineRange): number[] {
  const minimum = (range.end - range.start) / 6;
  const intervals = [
    1_000, 5_000, 10_000, 30_000, 60_000, 300_000, 900_000, 1_800_000, 3_600_000, 10_800_000,
    21_600_000, 43_200_000, 86_400_000, 604_800_000, 2_592_000_000,
  ];
  const step =
    intervals.find((value) => value >= minimum) ?? Math.ceil(minimum / 86_400_000) * 86_400_000;
  const ticks: number[] = [];
  for (let tick = Math.ceil(range.start / step) * step; tick < range.end; tick += step)
    ticks.push(tick);
  return ticks;
}
